/** @jsxImportSource @emotion/react */
import { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectionMode, CellRange } from '@grapecity/wijmo.grid';
import { Box } from 'models/setup/mobile/BoxLocation';
import { getBoxDetail } from 'apis/setup/mobile/BoxLocation';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import GridContainer from 'components/grids/GridContainer';
import _ from 'lodash';
import useEvent from 'react-use-event-hook';

/**
 * @desc 하역장관리 > 하역장 Map 조회 > 박스 목록 Grid
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.02.23   jihwan89        위도/경도 컬럼 삭제, Box입고일자, 바코드관리Y/N, 업체명 컬럼 추가
 * 2025.02.23   jihwan89        위도/경도 컬럼 오른쪽 으로 수정, 박스 상태 추가
 * 2025.02.26   jihwan89        컬럼 sorting, 필터 사용 후 row 클릭했을때 row선택 위치 오류 수정
 * 2026.03.12   jihwan89        하역장 맵 도착완료, 도착완료(수동), 이상처리 아닌 상태에서 위도, 경도, 구역명 값 안보이도록 수정
 */

interface BoxLocationGridProps {
  boxes: Box[];
  onBoxSelect?: (box: Box | null) => void;
  selectedBoxId?: string; // 외부에서 선택된 boxId (마커 클릭 시)
}

const BoxLocationGrid = ({ boxes, onBoxSelect, selectedBoxId }: BoxLocationGridProps) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      isReadOnly: true,
      cellTemplate: (grid: any) => grid.row._idx + 1,
    },
    {
      header: String(t('setup.label.박스No', '박스No')),
      binding: 'boxNo',
      width: 120,
      align: 'left',
      isReadOnly: true,
    },
    {
      header: String(t('setup.label.품목 목록명', '품목 목록명')),
      binding: 'pakBoxNm',
      width: 150,
      align: 'left',
      isReadOnly: true,
    },
    {
      header: String(t('M-CODE', 'M-CODE')),
      binding: 'eqpIds',
      width: 150,
      align: 'left',
      isReadOnly: true,
    },
    {
      header: String(t('setup.label.설비명', '설비명')),
      binding: 'eqpNms',
      width: 200,
      align: 'left',
      isReadOnly: true,
    },
    {
      header: String(t('setup.label.Box입고일자', 'Box입고일자')),
      binding: 'unloDtm',
      width: 160,
      align: 'center',
      isReadOnly: true,
    },
    {
      header: String(t('setup.label.바코드관리', '바코드관리')),
      binding: 'bcdYn',
      width: 110,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'boxStatCdNm',
      header: String(t('com.label.상태', '상태')),
      width: 120,
      isReadOnly: true,
    },
    {
      binding: 'boxStatCd',
      header: String(t('com.label.상태', '상태')),
      width: 120,
      isReadOnly: true,
      visible: false,
    },
    {
      header: String(t('setup.label.업체명', '업체명')),
      binding: 'excpNms',
      width: 200,
      align: 'center',
      isReadOnly: true,
    },
    {
      header: String(t('setup.label.위치이동여부', '위치이동여부')),
      binding: 'unloChgYn',
      width: 120,
      align: 'center',
      isReadOnly: true,
    },
    {
      header: String(t('setup.grid.구역명', '구역명')),
      binding: 'zonNm',
      width: 150,
      align: 'left',
      isReadOnly: true,
    },
    {
      header: String(t('setup.label.하역일시', '하역일시')),
      binding: 'unloDtmWeb',
      width: 150,
      align: 'center',
      isReadOnly: true,
      visible: false,
    },
    {
      header: String(t('setup.label.하역일시', '설명')),
      binding: 'rmk',
      width: '*',
      align: 'left',
      isReadOnly: true,
      visible: false,
    },
    {
      header: String(t('setup.grid.위도', '위도')),
      binding: 'xcrd',
      width: 130,
      align: 'right',
      format: 'n6',
      isReadOnly: true,
    },
    {
      header: String(t('setup.label.경도', '경도')),
      binding: 'ycrd',
      width: 130,
      align: 'right',
      format: 'n6',
      isReadOnly: true,
    },
  ];

  // row 선택 시 - API 호출하여 상세 데이터 조회
  const handleRowSelect = async (box: Box) => {
    if (box.boxId) {
      try {
        const detailBox = await getBoxDetail(box.boxId);
        if (detailBox) {
          onBoxSelect && onBoxSelect(detailBox);
        } else {
          onBoxSelect && onBoxSelect(box);
        }
      } catch (error) {
        console.error('박스 상세 조회 실패:', error);
        onBoxSelect && onBoxSelect(box);
      }
    } else {
      onBoxSelect && onBoxSelect(box);
    }
  };

  const onInitialized = (grid: any) => {
    setFlexRef(grid);
    gridRef.current = grid;

    // CustomGrid가 mousedown에서 SelectionMode.Cell로 변경하므로, 다시 Row로 복원
    grid.hostElement.addEventListener('mousedown', () => {
      setTimeout(() => {
        grid.selectionMode = SelectionMode.Row;
      }, 0);
    });
  };

  // 외부에서 selectedBoxId가 변경되면 그리드 row 선택 (마커 클릭 시)
  const isExternalSelectRef = useRef(false);

  // Wijmo Grid용 - row 선택 시 (자동 선택 무시)
  const onSelectionChanged = useMemo(() => {
    let isFirstCall = true;

    return (grid: any, e: any) => {
      if (e.row < 0) {
        onBoxSelect && onBoxSelect(null);
        return;
      }

      // 첫 호출(자동 선택)은 무시
      if (isFirstCall) {
        isFirstCall = false;
        return;
      }

      // 외부 선택(마커 클릭)에 의한 호출은 무시 (이미 부모에서 처리됨)
      if (isExternalSelectRef.current) {
        return;
      }

      const selectedItem = grid.rows[e.row].dataItem;
      handleRowSelect(selectedItem);
    };
  }, [boxes]);

  useEffect(() => {
    if (!flexRef || !selectedBoxId || !boxes.length) return;

    // boxes 배열에서 해당 boxId의 index 찾기
    const rowIndex = flexRef.collectionView.items.findIndex((box) => box.boxId === selectedBoxId);
    if (rowIndex < 0) return;

    // 현재 선택된 row와 같으면 무시
    if (flexRef.selection.row === rowIndex) return;

    // 외부 선택 플래그 설정 (selectionChanged에서 중복 호출 방지)
    isExternalSelectRef.current = true;

    // 그리드 row 선택
    flexRef.select(new CellRange(rowIndex, 0, rowIndex, flexRef.columns.length - 1));
    // 해당 row로 스크롤
    flexRef.scrollIntoView(rowIndex, 0);

    // 플래그 초기화
    setTimeout(() => {
      isExternalSelectRef.current = false;
    }, 0);
  }, [selectedBoxId, flexRef, boxes]);

  // 26.03.12 하역장 맵 도착완료, 도착완료(수동), 이상처리 아닌 상태에서 위도, 경도, 구역명 값 안보이도록 수정
  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (boxes.length > 0) {
      panel.rows.forEach((r) => {
        if (r.dataItem !== null) {
          const status = ['ABNORMAL', 'ARRIVAL_DONE', 'ARRIVAL_MANUAL'].includes(
            r.dataItem.boxStatCd
          );
          if (!status) {
            r.dataItem.zonNm = '';
            r.dataItem.xcrd = '';
            r.dataItem.ycrd = '';
          }
        }
      });
    }
  });

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.박스 목록', '박스 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{boxes?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>
      <GridContainer
        layoutDefinition={layoutDefinition}
        rowData={boxes}
        deferResizing={false}
        initialized={onInitialized}
        selectionMode="Row"
        selectionChanged={onSelectionChanged}
        itemFormatter={onItemFormatter}
        excludePin={['no']}
        excludeFilter={['no']}
        isSelector={false}
        align={'center'}
      />
    </>
  );
};

export default BoxLocationGrid;
